/**
 * Feature flags context — wraps tenant routes so any component can do:
 *
 *     const { isEnabled } = useFeatureFlags();
 *     if (isEnabled('notify_operator')) { ... }
 *
 * On mount it fetches `GET /feature-flags/resolved` (relative to the axios
 * instance's baseURL which already ends with `/api`) once and caches the
 * result in memory + sessionStorage. Refetches on tenant switch (when `user`
 * id changes) and on window focus (so admins don't have to refresh the tab
 * after toggling a flag).
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

const FeatureFlagsContext = createContext(null);

const STORAGE_KEY = '_ff_resolved';

const readCache = (userId) => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { env: 'unknown', flags: {} };
    const parsed = JSON.parse(raw);
    // Invalidate cache if it belongs to a different user/tenant
    if (parsed._uid && parsed._uid !== userId) return { env: 'unknown', flags: {} };
    return parsed;
  } catch {
    return { env: 'unknown', flags: {} };
  }
};

const writeCache = (data, userId) => {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, _uid: userId })); } catch { /* noop */ }
};

export const FeatureFlagsProvider = ({ children }) => {
  const { api, user, token } = useAuth();
  const uid = user?.id || user?.client_id || '';
  const [state, setState] = useState(() => readCache(uid));
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.get('/feature-flags/resolved');
      const next = { env: res.data?.env || 'unknown', flags: res.data?.flags || {} };
      setState(next);
      writeCache(next, uid);
    } catch {
      // Auth errors etc — keep stale cache, frontend defaults to off for unknown keys.
    } finally {
      setLoaded(true);
    }
  }, [api, token, uid]);

  // Re-fetch when user/tenant changes — also clear stale cache immediately
  useEffect(() => {
    const cached = readCache(uid);
    setState(cached);
    refresh();
  }, [refresh, uid]);

  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const isEnabled = useCallback((key) => Boolean(state.flags?.[key]), [state]);

  return (
    <FeatureFlagsContext.Provider value={{ env: state.env, flags: state.flags, isEnabled, refresh, loaded }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    // Sensible default — feature flags fail closed.
    return { env: 'unknown', flags: {}, isEnabled: () => false, refresh: async () => {}, loaded: false };
  }
  return ctx;
};
